import { lineDrawing } from './picture-tiles'
import { api, ApiError, changeNow, queuePartition } from './api'
import { firstName } from './late-message'
import { regularChoiceOn, regularChoiceWords } from './enquiry-regular-choice'
import type { Lead } from './types'
import { el, money, toast } from './ui'
import './enquiry-regular-offers.css'

interface Offer { id: number; version: number; state: string; generation: number; wa_status?: string | null; wa_error?: string | null }
interface Facts { replyUpto: number; key: string; words: string; nextAt: string | null; paymentFirst: boolean; prices: { tiles: {weeks: number; pence: number; first: number}[]; once: number; onceFirst: number } }
interface Held { id: number; offerId: number; offerVersion: number; days: number | null; pence: number; first: number; why: string | null }
interface Preview { enabled: boolean; facts?: Facts | null; why?: string; offer?: Offer | null; held?: Held[]; lead_version: number; choiceVersion?: number | null }
const pathOf = (l: Lead): string => `/api/leads/${l.id}/regular-offers`
const who = (l: Lead): string => firstName(l.name) || l.name

// All controls are mounted only after a fresh server approval gate, and are tied to this signed-in account and lead.
export function mountEnquiryRegularOffer(lead: Lead, target: HTMLElement, makeTile: () => HTMLButtonElement, reload: () => void): void {
  if (!lead.regular_offers_enabled) return
  const identity = queuePartition()
  let mounted: HTMLElement | null = null
  let mountedTile: HTMLElement | null = null
  let readSeq = 0
  const same = (): boolean => identity !== null && queuePartition() === identity && target.isConnected
  const read = (): void => {
    const seq = ++readSeq
    void api<Preview>(`${pathOf(lead)}?upto=${lead.last_in_id ?? 0}`).then(preview => {
      if (!same() || seq !== readSeq) return
      mounted?.remove(); mountedTile?.remove(); mountedTile = null
      if (!preview.enabled) return
      if (preview.lead_version !== lead.version) { reload(); return }
      const area = el('div', { class: 'enquiry-regular-controls' })
      mounted = area
      const note = el('p', { class: 'form-error enquiry-regular-error', role: 'alert' })
      const post = async (body: Record<string, unknown>, button: HTMLButtonElement): Promise<void> => {
        button.disabled = true; note.textContent = ''
        try { await changeNow('POST', pathOf(lead), body); if (!same()) return; reload() }
        catch (err) { if (!same()) return; button.disabled = false; note.textContent = err instanceof Error ? err.message : 'That did not save. Check your signal.' }
      }
      const selected = regularChoiceOn(lead), o = preview.offer
      if (selected) {
        const undo = el('button', { type: 'button', class: 'link-button enquiry-regular-undo' }, 'Undo their choice') as HTMLButtonElement
        undo.addEventListener('click', () => {
          // The selected row can be older than the last offer after a held old tap was explicitly used.
          const version = preview.choiceVersion ?? selected.offer_version
          if (!version) { note.textContent = 'Look again before undoing their choice.'; return }
          void post({ action: 'undo', offer_id: selected.offer_id, offer_version: version, lead_version: lead.version }, undo)
        })
        area.append(el('div', { class: 'enquiry-regular-result' },
          el('strong', {}, `${who(lead)} picked ${regularChoiceWords(selected.days)}`),
          el('p', {}, `${money(selected.pence)} a clean · first clean ${money(selected.first)}`),
          el('p', { class: 'muted' }, 'No day booked yet. Book them in when you have arranged it.'), undo))
      }
      if (o && ['sending','sent','unknown'].includes(o.state)) {
        const words = o.state === 'sending' ? `Sending to ${who(lead)} on WhatsApp…` : o.state === 'unknown' ? "Roundly isn't sure it went. Check WhatsApp on your phone before sending another message." : `Sent on WhatsApp · waiting for ${who(lead)}'s tap`
        const check = el('button', { type: 'button', class: 'link-button enquiry-regular-check' }, 'Check again') as HTMLButtonElement
        check.addEventListener('click', read)
        const state = el('div', { class: 'enquiry-regular-result enquiry-regular-waiting' }, el('p', {}, words), check)
        if (o.state !== 'sending') {
          const close = el('button', { type: 'button', class: 'link-button enquiry-regular-withdraw' }, o.state === 'unknown' ? 'I checked, close this offer' : 'Close this offer') as HTMLButtonElement
          close.addEventListener('click', () => void post({ action: 'withdraw', offer_id: o.id, offer_version: o.version }, close))
          state.append(close)
        }
        area.append(state)
      } else if (o?.state === 'failed') area.append(el('p', { class: 'enquiry-regular-result' }, `It didn't go. ${o.wa_error ?? 'Open the offer and look again before sending.'}`))
      for (const t of preview.held ?? []) {
        const savedSame = !!selected && selected.offer_id === t.offerId
        const use = el('button', { type: 'button', class: 'link-button enquiry-regular-use' }, 'Use it') as HTMLButtonElement
        use.disabled = savedSame
        use.addEventListener('click', () => void post({ action: 'use_tap', tap_id: t.id, offer_id: t.offerId, offer_version: t.offerVersion, lead_version: lead.version }, use))
        area.append(el('div', { class: 'enquiry-regular-result enquiry-regular-held' },
          el('strong', {}, `${who(lead)} tapped ${regularChoiceWords(t.days)}`), el('p', {}, `${money(t.pence)} a clean · first clean ${money(t.first)}`),
          el('p', { class: 'muted' }, savedSame ? 'Undo their saved choice first, then Use it.' : 'The offer or enquiry changed, or the tap came after seven days. Use it?'), use))
      }
      area.append(note)
      const pending = o && ['sending','sent','unknown'].includes(o.state)
      if (preview.facts && !selected && !pending) {
        const tile = makeTile()
        mountedTile = tile
        tile.addEventListener('click', () => openPreview(lead, preview.facts!, reload))
        // Put the tile straight after Book, before Send a quote, on D and P2.
        target.insertBefore(tile, target.querySelector('.enquiry-send-quote, .enquiry-follow-open'))
        area.append(el('span', { class: 'sr-only' }, 'Offer regular cleans is available.'))
      }
      target.prepend(area)
    }, () => { /* Without a fresh gate, nothing new is shown. Existing Book is always usable. */ })
  }
  read()
}

function openPreview(lead: Lead, facts: Facts, reload: () => void): void {
  if (document.querySelector('dialog.enquiry-regular-sheet[open]')) return
  const identity = queuePartition(), dialog = el('dialog', { class: 'booking-dialog quick-sheet enquiry-regular-sheet', 'aria-labelledby': 'enquiry-regular-title' }) as HTMLDialogElement
  const close = el('button', { type: 'button', class: 'close-disc', 'aria-label': 'Close' }, lineDrawing('cross', 16)) as HTMLButtonElement
  const not = el('button', { type: 'button', class: 'btn-secondary' }, 'Not now') as HTMLButtonElement
  const send = el('button', { type: 'button', class: 'btn-primary enquiry-regular-send' }, 'Send on WhatsApp') as HTMLButtonElement
  const note = el('p', { class: 'form-error', role: 'alert' })
  const words = el('div', { class: 'enquiry-regular-words', 'aria-label': 'The message' }, facts.words)
  const labels = [...facts.prices.tiles.map(t => `Every ${t.weeks} weeks`), 'Just occasionally']
  const buttons = el('div', { class: 'enquiry-regular-labels', 'aria-label': 'Their three buttons' }, ...labels.map(label => el('div', {}, label)))
  const body = el('div', { class: 'enquiry-regular-body' }, el('p', { class: 'muted' }, `${lead.name}${lead.address ? ` · ${lead.address}` : ''}`), words, buttons,
    el('p', { class: 'muted' }, 'Outside prices only. Any agreed insides or extras stay separate. Their tap saves a choice only. You book the day.'), note)
  const footer = el('div', { class: 'enquiry-regular-footer' }, send, not)
  dialog.append(el('header', { class: 'enquiry-regular-head' }, el('h2', { id: 'enquiry-regular-title' }, 'Offer regular cleans'), close), body, footer)
  const shut = (): void => dialog.close()
  close.addEventListener('click', shut); not.addEventListener('click', shut)
  dialog.addEventListener('close', () => dialog.remove())
  if (facts.paymentFirst) { note.textContent = 'Send the payment message for this number in Owed first.'; send.disabled = true }
  else if (facts.nextAt) {
    const at = new Date(`${facts.nextAt.replace(' ', 'T')}Z`)
    note.textContent = `They had a message from Roundly in the last day. This can go from ${at.toLocaleString('en-GB', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}.`
    send.disabled = true
  }
  const clientKey = crypto.randomUUID().replace(/-/g, '')
  send.addEventListener('click', () => {
    if (send.dataset.busy || send.disabled) return
    send.dataset.busy = '1'; send.disabled = true; close.disabled = true; not.disabled = true; send.textContent = 'Sending…'; note.textContent = ''
    void changeNow<{row: Offer}>('POST', pathOf(lead), { action: 'send', facts_key: facts.key, client_key: clientKey, reply_upto: facts.replyUpto }).then(result => {
      if (queuePartition() !== identity) { shut(); return }
      toast(result.row.state === 'sent' || result.row.state === 'answered' ? `Sent to ${who(lead)} on WhatsApp.` : result.row.state === 'failed' ? "It didn't go. Look at their card." : "Roundly isn't sure it went. Look at their card.")
      shut(); reload()
    }, (err: ApiError) => {
      if (queuePartition() !== identity) { shut(); return }
      delete send.dataset.busy; close.disabled = false; not.disabled = false; send.disabled = err.status === 409; send.textContent = 'Send on WhatsApp'
      note.textContent = err.status === 0 ? 'No signal. The preview is still here. Try again when you have signal.' : err.message
    })
  })
  document.body.append(dialog); dialog.showModal()
}
